import React, { createContext, useContext, ReactNode } from 'react';
import { getStageConfig, StageConfig } from '../config/AppConfig';

/**
 * App configuration context type
 */
interface AppConfigContextType {
  stage: string;
  config: StageConfig;
}

/**
 * Create the context
 */
const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

/**
 * AppConfigProvider props
 */
interface AppConfigProviderProps {
  stage: string;
  children: ReactNode;
}

/**
 * AppConfigProvider component
 * Provides stage-specific configuration to the application
 */
export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ stage, children }) => {
  const config = getStageConfig(stage);
  
  const value: AppConfigContextType = {
    stage,
    config
  };
  
  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
};

/**
 * Hook to use app configuration
 */
export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  
  return context;
};

/**
 * Hook to get the current stage
 */
export const useStage = (): string => {
  const { stage } = useAppConfig();
  return stage;
};

/**
 * Hook to get stage configuration
 */
export const useStageConfig = (): StageConfig => {
  const { config } = useAppConfig();
  return config;
};

/**
 * Hook to check if mock data is enabled
 */
export const useMockData = (): boolean => {
  const { config } = useAppConfig();
  return config.ENABLE_MOCK_DATA ?? false;
};

/**
 * Hook to get an API endpoint by name
 */
export const useApiEndpoint = (apiName: string): string | undefined => {
  const { config } = useAppConfig();
  const endpoint = config.API_GATEWAY_ENDPOINTS.endpoints.find(
    (ep: { name: string; endpoint: string }) => ep.name === apiName
  );
  return endpoint?.endpoint;
};
